import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@produzionejb/ui';
import { useMpgList, useDeleteMpg, useCreateMpg } from '../../lib/mpg-api';

const PAGE_SIZE = 50;

function formatDate(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}/${d.getUTCFullYear()}`;
}

export function MpgListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<'' | 'DRAFT' | 'CLOSED'>('');
  const [page, setPage] = useState(1);

  const { data, isLoading, error } = useMpgList({
    page,
    pageSize: PAGE_SIZE,
    ...(statusFilter && { status: statusFilter }),
  });

  const deleteMut = useDeleteMpg();
  const createMut = useCreateMpg();

  async function handleNewToday() {
    try {
      const created = await createMut.mutateAsync({ assignments: [] });
      navigate(`/documents/mpg/${created.id}/edit`);
    } catch (err) {
      window.alert((err as Error).message);
    }
  }

  async function handleDelete(id: string, label: string) {
    if (!window.confirm(t('mpg.list.confirmDelete', { name: label }))) return;
    try {
      await deleteMut.mutateAsync(id);
    } catch (err) {
      window.alert((err as Error).message);
    }
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-slate-900">{t('mpg.list.title')}</h2>
        <Button onClick={handleNewToday} disabled={createMut.isPending}>
          {t('mpg.list.newButton')}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('products.list.filtersTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-4">
          <div>
            <label className="text-xs text-slate-500">{t('mpg.fields.status')}</label>
            <select
              className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as '' | 'DRAFT' | 'CLOSED');
                setPage(1);
              }}
            >
              <option value="">{t('common.all')}</option>
              <option value="DRAFT">{t('mpg.list.statusDraft')}</option>
              <option value="CLOSED">{t('mpg.list.statusClosed')}</option>
            </select>
          </div>
          <div className="flex items-end sm:col-span-3">
            <span className="text-sm text-slate-600">
              {data ? t('mpg.list.totalCount', { count: data.total }) : ''}
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading && <p className="p-4 text-sm text-slate-500">{t('common.loading')}</p>}
          {error && (
            <p className="p-4 text-sm text-red-600">
              {t('mpg.list.errorLoad')}: {(error as Error).message}
            </p>
          )}
          {data && data.items.length === 0 && (
            <p className="p-4 text-sm text-slate-500">{t('mpg.list.empty')}</p>
          )}
          {data && data.items.length > 0 && (
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-600">
                <tr>
                  <th className="px-4 py-2">{t('mpg.fields.code')}</th>
                  <th className="px-4 py-2">{t('mpg.fields.workDate')}</th>
                  <th className="px-4 py-2">{t('mpg.fields.status')}</th>
                  <th className="px-4 py-2 text-right">{t('mpg.fields.movementsCount')}</th>
                  <th className="px-4 py-2 text-right">{t('mpg.fields.assignmentsCount')}</th>
                  <th className="px-4 py-2 text-right">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((d) => {
                  const label = d.code ? `MPG ${d.code}` : '—';
                  return (
                    <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-2 font-mono text-xs">{label}</td>
                      <td className="px-4 py-2 text-slate-700">{formatDate(d.workDate)}</td>
                      <td className="px-4 py-2">
                        <span
                          className={
                            d.status === 'DRAFT'
                              ? 'rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-700'
                              : 'rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700'
                          }
                        >
                          {d.status === 'DRAFT' ? t('mpg.list.statusDraft') : t('mpg.list.statusClosed')}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right font-mono text-xs">{d.movementsCount}</td>
                      <td className="px-4 py-2 text-right font-mono text-xs">{d.assignmentsCount}</td>
                      <td className="px-4 py-2 text-right">
                        <Link to={`/documents/mpg/${d.id}/edit`} className="mr-3 text-brand-700 hover:underline">
                          {d.status === 'DRAFT' ? t('common.edit') : t('common.view')}
                        </Link>
                        {d.status === 'DRAFT' && (
                          <button
                            type="button"
                            onClick={() => handleDelete(d.id, label)}
                            className="text-red-600 hover:underline"
                            disabled={deleteMut.isPending}
                          >
                            {t('common.delete')}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {data && totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            ‹
          </Button>
          <span className="text-sm text-slate-600">
            {t('products.list.pageOf', { page, totalPages })}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            ›
          </Button>
        </div>
      )}
    </div>
  );
}
